import React, {useContext, useEffect, useState} from "react";
import {Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Link, TextField, Tooltip, Typography} from "@material-ui/core";
import {MainContext} from "contexts/main.context";
import {useArticulosService} from "services/hooks";
import {INITIAL_FILTER_SETTINGS_STATE} from "components/helpers/initialFilterSettingsState";
import ImgNotFoundIcon from "@material-ui/icons/PhotoCameraRounded";
import {formatPrice, formatQuantity, InputInteger} from "assets/utils";
import {EditRounded, SecurityRounded} from "@material-ui/icons";
import {useSnackbar} from "notistack";
import QRCode from "react-qr-code";
import {ArticuloEquivalencias} from "components/helpers/equivalencias/articuloEquivalencias";

export const ArticuloDetailsDialog = (props) => {
	return (
		<Dialog
			open={props.open}
			onClose={props.onCancel}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			className='articulo-details-dialog'
		>
			<ArticuloDetailsDialogContent {...props}/>
		</Dialog>
	);
}

/**
 * Campo de la ficha: rótulo arriba en gris, valor abajo en negro.
 */
const Campo = ({label, children}) => (
	<Box display={"flex"} flexDirection={"column"}>
		<Typography variant={"caption"} color={"textSecondary"}>{label}</Typography>
		<Box>{children}</Box>
	</Box>
);

/**
 * Título de sección de la ficha.
 */
const TituloSeccion = ({children}) => (
	<Typography variant={"body2"} component={'div'} className={'mb-2'} style={{fontWeight: 'bold'}}>
		{children}
	</Typography>
);

// Cuántos caracteres de las aplicaciones se muestran antes de recortar.
const LARGO_APLICACIONES = 220;

/**
 * Aplicaciones del artículo (N_articulo_ext.aplicacion): en qué vehículos/modelos se
 * usa. Pueden llegar a varios miles de caracteres, así que se muestran recortadas
 * con un link para expandirlas.
 */
const Aplicaciones = ({texto}) => {
	const [expandido, setExpandido] = useState(false);

	// La mayoría de los artículos todavía no tienen aplicaciones cargadas.
	if (!texto)
		return (
			<Typography variant={"body2"} color={"textSecondary"} component={'div'}>
				Sin aplicaciones
			</Typography>
		);

	const recortable = texto.length > LARGO_APLICACIONES;

	// Corto en el último espacio anterior al límite para no partir una palabra al medio.
	// Si no hay ninguno cerca (un código largo sin espacios) corto seco en el límite.
	const recortar = () => {
		const corte = texto.substring(0, LARGO_APLICACIONES);
		const ultimoEspacio = corte.lastIndexOf(' ');
		const recorte = ultimoEspacio > LARGO_APLICACIONES * 0.7 ? corte.substring(0, ultimoEspacio) : corte;
		return recorte.trim() + '...';
	};

	return (
		<Typography variant={"body2"} component={'div'} style={{whiteSpace: 'pre-line'}}>
			{recortable && !expandido ? recortar() : texto}
			{recortable && (
				<Link
					href={'#'}
					onClick={e => {
						e.preventDefault();
						setExpandido(!expandido);
					}}
					variant={"body2"}
					color={"primary"}
					className={'ml-1'}
					style={{fontWeight: 500, whiteSpace: 'nowrap'}}
				>
					{expandido ? 'ocultar' : 'ver más'}
				</Link>
			)}
		</Typography>
	);
};

const ArticuloDetailsDialogContent = ({open, id, onCancel}) => {
	// El id es mutable: al clickear una equivalencia se reemplaza el artículo que se
	// está mostrando, en vez de apilar otro popup encima.
	const [idActual, setIdActual] = useState(id);
	// undefined = todavía cargando, null = el artículo no tiene foto, string = base64 de la foto.
	const [image, setImage] = useState(undefined);
	const [averageSells, setAverageSells] = useState(0);
	const [securityStockEditing, setSecurityStockEditing] = useState(false);
	const [qrDialogOpen, setQrDialogOpen] = useState(false);
	const [aplicacionesEditing, setAplicacionesEditing] = useState(false);
	const [articulo, setArticulo] = useState({});
	const [securityStockValue, setSecurityStockValue] = useState(articulo.stocksval);
	const [aplicacionesValue, setAplicacionesValue] = useState('');

	const context = useContext(MainContext);
	const ArticulosService = useArticulosService();
	const {enqueueSnackbar} = useSnackbar();
	const moment = require('moment');
	const quantityOfMonths = 3;

	const qrValue = `${articulo.codigobarr || articulo.id || ''}`;
	// Mismos roles que habilita la API en articulos/editAplicaciones.
	const puedeEditarAplicaciones = ["A", "O"].includes(context.loggedUser.role);

	// Si el padre abre el diálogo para otro artículo, vuelvo a arrancar por ese.
	useEffect(() => {
		setIdActual(id);
	}, [id]);

	useEffect(() => {
		ArticulosService.getById(
			idActual,
			null,
			response => setArticulo(response.data.result),
			error => enqueueSnackbar(error, {variant: 'error'})
		);
	}, [idActual]);

	useEffect(() => {
		if (!articulo.id)
			return;

		// Al pasar a otro artículo (equivalencias) vuelvo a "cargando" para no dejar
		// visible la foto del anterior, y descarto la respuesta si ya cambió el id.
		setImage(undefined);
		let vigente = true;

		ArticulosService.getImage(
			articulo.id,
			response => vigente && setImage(response || null),
			error => {
				if (vigente) setImage(null);
				enqueueSnackbar(error, {variant: 'error'});
			}
		);

		return () => vigente = false;
	}, [articulo]);

	useEffect(() => {
		if (!articulo.id)
			return;

		const articulos = {all: false, selected: [articulo.id]};
		const dateFrom = moment().subtract(quantityOfMonths, 'month');

		const settings = {...INITIAL_FILTER_SETTINGS_STATE};
		settings.articulos = articulos;
		settings.dates = {...settings.dates, from: dateFrom};

		ArticulosService.getAverageSells(
			settings,
			quantityOfMonths,
			response => {
				if (!!response.data.result[articulo.id])
					setAverageSells(response.data.result[articulo.id]);
				else
					setAverageSells(0);
			},
			error => enqueueSnackbar(error, {variant: 'error'})
		);
	}, [articulo]);

	useEffect(() => {
		setSecurityStockValue(articulo.stocksval);
	}, [articulo]);

	// Al cambiar de artículo (equivalencias) descarto lo que se estuviera editando.
	useEffect(() => {
		setAplicacionesValue(articulo.aplicaciones || '');
		setAplicacionesEditing(false);
	}, [articulo]);

	const onConfirmAplicaciones = () => {
		ArticulosService.editAplicaciones(
			articulo.id,
			aplicacionesValue,
			response => {
				setArticulo(response.data.result.articulo);
				enqueueSnackbar("Aplicaciones editadas correctamente", {variant: 'success'});
				setAplicacionesEditing(false);
			},
			error => enqueueSnackbar(error, {variant: 'error'})
		)
	};
	const onCancelAplicaciones = () => {
		setAplicacionesValue(articulo.aplicaciones || '');
		setAplicacionesEditing(false);
	};

	const securityStockStartEditing = () => setSecurityStockEditing(true);
	const securityStockEndEditing = () => setSecurityStockEditing(false);

	const onConfirmSecurityStock = () => {
		ArticulosService.editSecurityStock(
			articulo.id,
			securityStockValue,
			response => {
				setArticulo(response.data.result.articulo);
				enqueueSnackbar("Stock de seguridad editado correctamente", {variant: 'success'});
				securityStockEndEditing();
			},
			error => enqueueSnackbar(error, {variant: 'error'})
		)
	};
	const onCancelSecurityStock = () => {
		setSecurityStockValue(articulo.stocksval);
		securityStockEndEditing();
	};

	return (
		<>
			<DialogTitle id="alert-dialog-title">
				<Typography variant={"body1"}>
					<Typography variant={"body2"} color={"textSecondary"} component={'span'}>Artículo: </Typography>
					{articulo.id}
				</Typography>
				<Typography variant={"body2"} color={"textSecondary"}>
					Detalle: {articulo.detalle}
				</Typography>
			</DialogTitle>
			<DialogContent>
				<Grid container className='mb-4 niquel-articulo-image'>
					{image === undefined && (
						<Box className='img-loading'>
							<CircularProgress size={32}/>
						</Box>
					)}
					{!!image && (
						<Box className='img-found'>
							<img src={`data:image/jpeg;base64,${image}`} alt={articulo.detalle}/>
						</Box>
					)}
					{image === null && (
						<Box className='img-not-found'>
							<ImgNotFoundIcon/>
						</Box>
					)}
				</Grid>
				<TituloSeccion>Propiedades:</TituloSeccion>
				<Box className={'mb-3'}>
					{/* El lápiz va pegado al rótulo y no debajo del texto: las aplicaciones
					    pueden ser un párrafo largo y ahí abajo el botón pasaba desapercibido. */}
					<Campo
						label={
							<>
								Aplicaciones
								{puedeEditarAplicaciones && !aplicacionesEditing &&
									<Tooltip title={'Editar aplicaciones'}>
										{/* fontSize small (20px) queda grande al lado de un rótulo de 12px. */}
										<IconButton size={"small"} className={'ml-1'} onClick={() => setAplicacionesEditing(true)}>
											<EditRounded style={{fontSize: '0.95rem'}}/>
										</IconButton>
									</Tooltip>
								}
							</>
						}
					>
						{aplicacionesEditing ?
							<>
								<TextField
									fullWidth
									multiline
									minRows={4}
									maxRows={16}
									variant={"outlined"}
									value={aplicacionesValue}
									onChange={e => setAplicacionesValue(e.target.value)}
								/>
								<Button variant={"text"} color={"primary"} size={"small"} onClick={onConfirmAplicaciones}>Guardar</Button>
								<Button variant={"text"} size={"small"} onClick={onCancelAplicaciones}>Cancelar</Button>
							</> :
							/* El key hace que al saltar a una equivalencia el texto vuelva a arrancar recortado. */
							<Aplicaciones texto={articulo.aplicaciones} key={articulo.id}/>
						}
					</Campo>
				</Box>
				{/* Precio de lista y marca ocupan las dos primeras de las cuatro columnas,
				    para que queden alineadas con los campos de unidades de abajo. */}
				<Grid container spacing={2}>
					<Grid item xs={3}>
						<Campo label={'Precio de lista'}>
							<Typography variant={"body2"} component={'span'}>{formatPrice(articulo.lista1, true)}</Typography>
						</Campo>
					</Grid>
					<Grid item xs={3}>
						<Campo label={'Marca'}>
							<Typography variant={"body2"} component={'span'}>{articulo.apodo || '-'}</Typography>
						</Campo>
					</Grid>
					<Grid item xs={3}/>
					<Grid item xs={3}/>
				</Grid>
				{/*<Grid item>*/}
				{/*	<Typography variant={"body2"} color={"textSecondary"} component={'span'}>Lista 2: </Typography>*/}
				{/*	<Typography variant={"body2"} component={'span'}>{formatPrice(articulo.lista2, true)}</Typography>*/}
				{/*</Grid>*/}
				{/*<Grid item>*/}
				{/*	<Typography variant={"body2"} color={"textSecondary"} component={'span'}>Lista 3: </Typography>*/}
				{/*	<Typography variant={"body2"} component={'span'}>{formatPrice(articulo.lista3, true)}</Typography>*/}
				{/*</Grid>*/}
				{/*<Grid item>*/}
				{/*	<Typography variant={"body2"} color={"textSecondary"} component={'span'}>Lista 4: </Typography>*/}
				{/*	<Typography variant={"body2"} component={'span'}>{formatPrice(articulo.lista4, true)}</Typography>*/}
				{/*</Grid>*/}
				{/*<Grid item>*/}
				{/*	<Typography variant={"body2"} color={"textSecondary"} component={'span'}>Lista 5: </Typography>*/}
				{/*	<Typography variant={"body2"} component={'span'}>{formatPrice(articulo.lista5, true)}</Typography>*/}
				{/*</Grid>*/}
				{/*<Grid item>*/}
				{/*	<Typography variant={"body2"} color={"textSecondary"} component={'span'}>Lista 6: </Typography>*/}
				{/*	<Typography variant={"body2"} component={'span'}>{formatPrice(articulo.lista6, true)}</Typography>*/}
				{/*</Grid>*/}
				<Grid container spacing={2} className={'mt-1'}>
					<Grid item xs={3}>
						<Campo label={'Stock Total'}>
							<Typography variant={"body2"} component={'span'}>{formatQuantity(articulo.total)}</Typography>
							<Typography variant={"caption"} component={'span'}> unid</Typography>
						</Campo>
					</Grid>
					<Grid item xs={3}>
						<Campo label={'Reservado'}>
							<Typography variant={"body2"} component={'span'}>{formatQuantity(articulo.reservado)}</Typography>
							<Typography variant={"caption"} component={'span'}> unid</Typography>
						</Campo>
					</Grid>
					<Grid item xs={3}>
						<Campo label={'Disponible'}>
							<Typography variant={"body2"} component={'span'}>{formatQuantity(articulo.disponible)}</Typography>
							<Typography variant={"caption"} component={'span'}> unid</Typography>
						</Campo>
					</Grid>
					<Grid item xs={3}>
						<Campo label={'Orden Compra'}>
							<Typography variant={"body2"} component={'span'}>{formatQuantity(articulo.ordencompra)}</Typography>
							<Typography variant={"caption"} component={'span'}> unid</Typography>
						</Campo>
					</Grid>
				</Grid>
				{/* Stock de seguridad y ventas promedio van a media columna: el primero
				    necesita lugar para el input y los botones de edición. */}
				<Grid container spacing={2} className={'mt-1'}>
					{(["A", "O"].includes(context.loggedUser.role)) && (
						<Grid item xs={6}>
							<Campo
								label={
									<>
										<SecurityRounded fontSize={"inherit"} style={{position: "relative", top: "2px"}} className={'mr-1'}/>
										Stock de Seguridad
									</>
								}
							>
								{securityStockEditing ?
									<>
										<TextField
											style={{width: '60px'}}
											value={securityStockValue}
											onChange={e => setSecurityStockValue(e.target.value)}
											InputProps={{
												inputComponent: InputInteger
											}}
										/>
										<Button variant={"text"} color={"primary"} size={"small"} onClick={onConfirmSecurityStock}>Guardar</Button>
										<Button variant={"text"} size={"small"} onClick={onCancelSecurityStock}>Cancelar</Button>
									</> :
									<>
										<Typography variant={"body2"} component={"span"}>{formatQuantity(securityStockValue)}</Typography>
										<Typography variant={"caption"} component={'span'}> unid</Typography>
										{["A"].includes(context.loggedUser.role) &&
											<Button variant={"text"} size={"small"} onClick={securityStockStartEditing}>Editar</Button>
										}
									</>
								}
							</Campo>
						</Grid>
					)}
					<Grid item xs={6}>
						<Campo label={`Ventas promedio (últimos ${quantityOfMonths} meses)`}>
							<Typography variant={"body2"} component={'span'}>{formatPrice(averageSells)}</Typography>
							<Typography variant={"caption"} component={'span'}> unid</Typography>
						</Campo>
					</Grid>
				</Grid>

				<Box className={'mt-4'}>
					<TituloSeccion>Equivalencias:</TituloSeccion>
					<ArticuloEquivalencias
						idArticulo={articulo.id}
						onNavigate={setIdActual}
					/>
				</Box>

				{!!qrValue && (
					<Box className={'mt-4'}>
						<TituloSeccion>Identificador:</TituloSeccion>
						<Box
							display={"inline-flex"}
							bgcolor={"#fff"}
							borderRadius={4}
							style={{cursor: "pointer", width: "fit-content"}}
							onClick={() => setQrDialogOpen(true)}
						>
							<QRCode
								value={qrValue}
								size={128}
								bgColor={"#FFFFFF"}
								fgColor={"#000000"}
							/>
						</Box>
						<Typography variant={"caption"} color={"textSecondary"} component={'div'} className={'mt-2'}>
							Click para ampliar
						</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button
					onClick={e => {
						e.stopPropagation();
						onCancel();
					}}
					color="primary"
				>
					Cerrar
				</Button>
			</DialogActions>

			<Dialog
				open={qrDialogOpen}
				onClose={() => setQrDialogOpen(false)}
				maxWidth={"xs"}
				fullWidth
			>
				<DialogTitle>Código QR</DialogTitle>
				<DialogContent>
					<Box
						p={2}
						display={"flex"}
						flexDirection={"column"}
						alignItems={"center"}
					>
						<Box p={2} bgcolor={"#fff"} borderRadius={4}>
							<QRCode
								value={qrValue}
								size={256}
								bgColor={"#FFFFFF"}
								fgColor={"#000000"}
							/>
						</Box>
						<Typography variant={"body2"} color={"textSecondary"} className={'mt-3'}>
							{qrValue}
						</Typography>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setQrDialogOpen(false)} color="primary">
						Cerrar
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}
